import { usePublishOperationReport } from '@shared/api/hooks/reports/reports';
import {
  CompanyReportStatusEnum,
  OperationReportList,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import Loader from '@shared/components/loader';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Card, CardContent, CardFooter } from '@shared/components/ui/card';
import { TableCell } from '@shared/components/ui/table';
import { useToast } from '@shared/components/ui/use-toast';
import { cn, formatDate, formatNumber } from '@shared/lib/utils';
import React from 'react';
import { ReportType } from '../types';
import { useGetReportTags } from '../utils';
import { ReportCardActions } from './report-card-actions';

interface ReportCard extends React.HTMLAttributes<HTMLDivElement> {
  reportType: ReportType;
  report: OperationReportList;
}

export default function ReportCard({
  report,
  className,
  onClick,
  reportType,
}: ReportCard) {
  const published =
    report.status === CompanyReportStatusEnum.published ||
    report.status === CompanyReportStatusEnum.edited;
  const publishing = report.status === CompanyReportStatusEnum.publishing;

  const tags = useGetReportTags(report);

  const reportInfoContent: {
    label: string;
    value: string | number;
  }[] = [
    {
      label: 'Created',
      value: formatDate(report.created, 'do MMMM yyyy'),
    },
    ...(report.lastPublishedAt
      ? [
          {
            label: 'Last Published',
            value: report.lastPublishedAt
              ? formatDate(report.lastPublishedAt, 'do MMMM yyyy')
              : '',
          },
        ]
      : []),
  ];

  return (
    <TableCell className={cn('p-0.6', className)}>
      <Card
        className={cn(
          'min-w-96 mb-3.5 mr-0 border-primary-50 transition-all cursor-default',
          {
            'hover:border-primary-200 hover:shadow-sm cursor-pointer': onClick,
          }
        )}
      >
        <CardContent className="items-center justify-between px-3 pb-2 pt-4">
          <div className="flex w-full justify-between">
            <div>
              <Text
                variant="cardTitle"
                className="mb-2 ml-2 w-full"
                data-testid="report-title"
              >
                {report.title}
              </Text>
            </div>
            {published && (
              <div className="flex items-baseline space-x-2">
                <Text className="text-2xl font-medium text-primary">
                  {formatNumber(report.totalEmissionsTonnes!)}
                </Text>
                <Unit variant="tCO2e" className="text-xl" />
              </div>
            )}
            {publishing && (
              <div className="flex items-center space-x-2">
                <Loader size="xs" />
                <Text className="pr-2 text-xs text-primary-600">
                  Publishing...
                </Text>
              </div>
            )}
          </div>
          {tags && tags.length > 0 && (
            <div className="ml-1 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <Badge key={tag} className="text-nowrap">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
        <CardContent className="flex items-center justify-between space-x-2 px-3 pt-4 ">
          <div className="flex w-full max-w-xl flex-row space-x-4">
            {reportInfoContent.map((info) => (
              <ReportInfo key={info.label} {...info} />
            ))}
          </div>
          <ReportCardActions report={report} reportType={reportType} />
        </CardContent>
        <CardFooter className="p-0">
          {(report.hasUnreviewedData ||
            report.status === CompanyReportStatusEnum.edited ||
            report.status === CompanyReportStatusEnum.failed) && (
            <ReportCardFooter report={report} reportType={reportType} />
          )}
        </CardFooter>
      </Card>
    </TableCell>
  );
}

interface ReportInfoProps {
  label: string;
  value: string | number;
  alignRight?: boolean;
  unit?: () => JSX.Element;
}
const ReportInfo = ({ label, value, alignRight, unit }: ReportInfoProps) => (
  <div className="ml-2 min-w-20 ">
    <Text variant="subtle">{label}</Text>
    <div className={cn('flex items-end', { 'justify-end': alignRight })}>
      <Text className="mr-1 text-sm">{value}</Text>
      {unit && unit()}
    </div>
  </div>
);

const ReportCardFooter = ({
  report,
  reportType,
}: {
  report: OperationReportList;
  reportType: ReportType;
}) => {
  const { toast } = useToast();
  const { mutateAsync: publishReport, isLoading: isPublishing } =
    usePublishOperationReport();

  const republishReport = async () => {
    try {
      await publishReport({
        operationReportUuid: report.uuid,
      });
      toast({
        title: 'Report published',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: `Error publishing report`,
        description: 'Please refresh and try again',
        variant: 'destructive',
      });
    }
  };

  // Determine footer state with precedence: unreviewed > edited > failed
  const footerState = React.useMemo(() => {
    if (report.status === CompanyReportStatusEnum.edited) {
      return {
        type: 'edited' as const,
        bgColor: 'bg-primary-50',
        iconColor: 'text-primary-700',
        textColor: 'text-primary-700',
        message: `Report is outdated, ${reportType} or modelling data has been updated`,
        buttonText: 'Republish report',
        buttonColor: '',
      };
    }
    if (report.status === CompanyReportStatusEnum.failed) {
      return {
        type: 'failed' as const,
        bgColor: 'bg-warning-50',
        iconColor: 'text-warning-800',
        textColor: 'text-warning-800',
        message: 'Publishing failed, please try to republish or get in touch',
        buttonText: 'Republish report',
        buttonColor: 'text-warning-800',
      };
    }
    return null;
  }, [report.status, reportType]);

  if (!footerState) return null;

  const handleAction = async () => {
    await republishReport();
  };

  return (
    <div
      className={cn(
        'size-full rounded-b-lg px-4 z-0 border-t border-primary-100',
        footerState.bgColor
      )}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center space-x-2">
          <Icons.warning className={cn('size-4', footerState.iconColor)} />
          <Text className={cn('text-sm', footerState.textColor)}>
            {footerState.message}
          </Text>
        </div>
        <Button
          variant="link"
          onClick={handleAction}
          disabled={isPublishing}
          className={footerState.textColor}
        >
          {footerState.buttonText}
        </Button>
      </div>
    </div>
  );
};
